import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {ChatReadWRefs, ChatListItem, ChatContext as ChatContextType} from "../schemas/chat";
import {getChat as getChatApi, createChat as createChatApi, getChatsForGroup} from "../api";
import {CreateTurnParams, fetchMessage, fetchMessagesByChat, streamChatTurn} from "../api/chat-messages";
import type {ChatMessageRead} from "../schemas/chat_message";
import type {AgentAction} from "../schemas/chat_message";
import {MessageStatus} from "../schemas/chat_message";
import type {DocumentRequestCreate, TagCreate} from "../schemas";
import {useUserContext} from "./user_context";
import {withApiErrorHandlerThrow} from "../error_handling";
import {useNavigationContext} from "./NavContext/nav-context";
import {useGroupContext} from "./group_context";
import {useJobContext} from "./job_context";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SendMessageParams {
    text: string;
    sentFromChatInputState?: string;
    chatContexts?: ChatContextType[];
    documentRequest?: DocumentRequestCreate;
    tagsToInfer?: TagCreate[];
}

interface AIState {
    getChat: (chatId: string) => Promise<ChatReadWRefs>;
    createChat: () => Promise<ChatReadWRefs>;
    getChatsForCurrentGroup: () => Promise<ChatListItem[]>;
    replaceChat: (newChat: ChatListItem) => void;

    chats: ChatListItem[];
    setChats: React.Dispatch<React.SetStateAction<ChatListItem[]>>;
    currentChat: ChatListItem | null;
    setCurrentChat: React.Dispatch<React.SetStateAction<ChatListItem | null>>;
    isLoading: boolean;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;

    // Message state
    messages: ChatMessageRead[];
    messagesLoading: boolean;
    loadMessages: (chatId: string) => Promise<void>;
    sendMessage: (params: SendMessageParams) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 1_500;
const TERMINAL = new Set<string>([MessageStatus.COMPLETE, MessageStatus.ERROR]);

const AIStateContext = createContext<AIState | undefined>(undefined);

export const useAIStateContext = () => {
    const context = useContext(AIStateContext);
    if (!context) {
        throw new Error("useAIStateContext must be used within an AIStateProvider");
    }
    return context;
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const AIStateProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const [isLoading, setIsLoading] = React.useState(true);
    const {currentUser} = useUserContext()!;
    const {navigateTo} = useNavigationContext()!;
    const {currentGroup} = useGroupContext()!;
    const jobContext = useJobContext();
    const [chats, setChats] = useState<ChatListItem[]>([]);
    const [currentChat, setCurrentChat] = useState<ChatListItem | null>(null);

    // -- Message state --
    const [messages, setMessages] = useState<ChatMessageRead[]>([]);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [inFlightIds, setInFlightIds] = useState<Set<string>>(new Set());
    const inFlightRef = useRef(inFlightIds);
    inFlightRef.current = inFlightIds;

    // -- Chat CRUD (unchanged) --

    const getChat = useCallback(async (chatId: string) => {
        setIsLoading(true);
        try {
            return await withApiErrorHandlerThrow(
                () => getChatApi(chatId),
                {titleQualifier: "Error Fetching Chat", navigateTo}
            );
        } finally {
            setIsLoading(false);
        }
    }, [navigateTo]);

    const createChat = useCallback(async () => {
        setIsLoading(true);
        try {
            if (!currentGroup) throw new Error("No current group selected");
            return await withApiErrorHandlerThrow(
                () => createChatApi(currentGroup.id, currentUser!.id),
                {titleQualifier: "Error Creating Chat", navigateTo}
            );
        } finally {
            setIsLoading(false);
        }
    }, [currentGroup, currentUser, navigateTo]);

    const getChatsForCurrentGroup = useCallback(async () => {
        setIsLoading(true);
        try {
            if (!currentGroup) throw new Error("No current group selected");
            return await withApiErrorHandlerThrow(
                () => getChatsForGroup(currentGroup.id),
                {titleQualifier: "Error Fetching Chats", navigateTo}
            );
        } finally {
            setIsLoading(false);
        }
    }, [currentGroup, navigateTo]);

    const replaceChat = useCallback(
        (newChat: ChatListItem) => {
            setChats(prevChats => {
                const exists = prevChats.some(chat => chat.id === newChat.id);
                return exists
                    ? prevChats.map(chat => (chat.id === newChat.id ? newChat : chat))
                    : [...prevChats, newChat];
            });
            setCurrentChat(prev => (prev?.id === newChat.id ? newChat : prev));
        },
        []
    );

    // -- Load messages for a chat (no auto-polling) --

    const loadMessages = useCallback(async (chatId: string) => {
        setMessagesLoading(true);
        try {
            const msgs = await fetchMessagesByChat(chatId);
            // Sort by creation time; when timestamps match, user before assistant
            msgs.sort((a, b) => {
                const dt = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                if (dt !== 0) return dt;
                // Tiebreaker: "user" (u) sorts before "assistant" (a) alphabetically,
                // but we want user first, so compare normally (u > a → positive)
                // Actually: 'a' < 'u', so assistant sorts first with localeCompare.
                // We want user first, so reverse: b.role compared to a.role
                return b.role.localeCompare(a.role);
            });
            setMessages(msgs);

            // Seed polling for any non-terminal assistant messages (e.g. page refresh)
            const nonTerminal = msgs.filter(
                m => m.role === "assistant" && m.state && !TERMINAL.has(m.state.status),
            );
            if (nonTerminal.length > 0) {
                setInFlightIds(new Set(nonTerminal.map(m => m.id)));
            }
        } finally {
            setMessagesLoading(false);
        }
    }, []);

    // -- Send a message, then reload authoritative state from server --

    const sendMessage = useCallback(
        async (params: SendMessageParams) => {
            if (!currentChat) throw new Error("No chat open");

            const chatId = currentChat.id;

            // Show optimistic user + pending assistant immediately
            const now = new Date().toISOString();
            const tempAssistantId = crypto.randomUUID();

            const userMsg: ChatMessageRead = {
                id: crypto.randomUUID(),
                chat_id: chatId,
                role: "user",
                sender_id: currentUser?.email ?? "unknown",
                content: {text: params.text, metadata: null},
                state: null,
                created_at: now,
            };

            const pendingAssistant: ChatMessageRead = {
                id: tempAssistantId,
                chat_id: chatId,
                role: "assistant",
                sender_id: "assistant",
                content: null,
                state: {status: MessageStatus.PENDING, actions: [], summary: null},
                created_at: now,
            };

            setMessages(prev => [...prev, userMsg, pendingAssistant]);

            const turnParams: CreateTurnParams = {
                chatId,
                content: params.text,
                senderId: currentUser?.email ?? "unknown",
                sentFromChatInputState: params.sentFromChatInputState,
                chatContexts: params.chatContexts,
                documentRequest: params.documentRequest,
                tagsToInfer: params.tagsToInfer,
            };

            // Accumulator for streaming text tokens
            let streamedText = "";

            try {
                await streamChatTurn(turnParams, {
                    onAction: (action: AgentAction) => {
                        setMessages(prev => prev.map(m => {
                            if (m.id !== tempAssistantId) return m;
                            const currentActions = m.state?.actions ?? [];

                            // Find existing action by name and update, or append new
                            const existingIdx = currentActions.findIndex(a => a.name === action.name);
                            let updatedActions: AgentAction[];
                            if (existingIdx >= 0) {
                                updatedActions = currentActions.map((a, i) => i === existingIdx ? action : a);
                            } else {
                                updatedActions = [...currentActions, action];
                            }

                            return {
                                ...m,
                                state: {
                                    status: MessageStatus.PROCESSING,
                                    actions: updatedActions,
                                    summary: null,
                                },
                            };
                        }));
                    },
                    onToken: (text: string) => {
                        streamedText += text;
                        const currentText = streamedText;
                        setMessages(prev => prev.map(m => {
                            if (m.id !== tempAssistantId) return m;
                            return {
                                ...m,
                                content: {
                                    ...m.content,
                                    text: currentText,
                                },
                            };
                        }));
                    },
                    onJob: (jobRead) => {
                        // Kick off the existing job polling system
                        jobContext?.addJobs([jobRead]);
                    },
                });

                // Stream done — reload all messages from server for authoritative ordering.
                // This replaces the optimistic messages (which had client-generated IDs)
                // with the real server messages in their correct creation order.
                const serverMsgs = await fetchMessagesByChat(chatId);
                serverMsgs.sort((a, b) => {
                    const dt = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                    if (dt !== 0) return dt;
                    return b.role.localeCompare(a.role);
                });
                setMessages(serverMsgs);

                // Refresh chat to pick up title (generated async from first message)
                try {
                    const freshChat = await getChatApi(chatId);
                    replaceChat(freshChat);
                } catch { /* non-critical */ }

                // Seed polling for any non-terminal assistant messages
                const nonTerminal = serverMsgs.filter(
                    m => m.role === "assistant" && m.state && !TERMINAL.has(m.state.status),
                );
                if (nonTerminal.length > 0) {
                    setInFlightIds(prev => {
                        const next = new Set(prev);
                        nonTerminal.forEach(m => next.add(m.id));
                        return next;
                    });
                }
            } catch (err) {
                // Replace placeholder with error state
                setMessages(prev => prev.map(m =>
                    m.id === tempAssistantId
                        ? {...m, state: {status: MessageStatus.ERROR, actions: [], summary: "Failed to send message"}}
                        : m
                ));
                throw err;
            }
        },
        [currentChat, currentUser],
    );

    // -- Poll in-flight messages (only runs when inFlightIds is non-empty) --

    useEffect(() => {
        if (inFlightIds.size === 0) return;

        const poll = async () => {
            const ids = Array.from(inFlightRef.current);
            if (ids.length === 0) return;

            const settled: string[] = [];

            await Promise.all(
                ids.map(async (id) => {
                    try {
                        const updated = await fetchMessage(id);
                        setMessages(prev => prev.map(m => (m.id === id ? updated : m)));

                        if (updated.state && TERMINAL.has(updated.state.status)) {
                            settled.push(id);
                        }
                    } catch (err) {
                        console.error(`Poll failed for message ${id}:`, err);
                    }
                }),
            );

            if (settled.length > 0) {
                setInFlightIds(prev => {
                    const next = new Set(prev);
                    for (const id of settled) next.delete(id);
                    return next;
                });
            }
        };

        // Poll immediately, then on interval
        poll();
        const interval = setInterval(poll, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [inFlightIds.size]);

    // -- Render --

    const contextValue = useMemo(() => ({
        getChat,
        createChat,
        getChatsForCurrentGroup,
        replaceChat,
        isLoading,
        setIsLoading,
        chats,
        setChats,
        currentChat,
        setCurrentChat,
        messages,
        messagesLoading,
        loadMessages,
        sendMessage,
    }), [
        getChat, createChat, getChatsForCurrentGroup, replaceChat,
        isLoading, chats, currentChat, messages, messagesLoading,
        loadMessages, sendMessage,
    ]);

    return (
        <AIStateContext.Provider value={contextValue}>
            {children}
        </AIStateContext.Provider>
    );
};
